import React from 'react';
import { CircleStackIcon, DocumentTextIcon } from '@heroicons/react/24/outline';

import {
  Badge,
  Panel,
  PanelBody,
  PanelHeader,
  PanelTitle,
  TabularDataViewer,
} from '../../components/ui';
import { useJudgmentalAccrualsWorkbook } from './api';

export default function SourceDataPage() {
  const { data: workbook, error: workbookError, isError, isLoading } = useJudgmentalAccrualsWorkbook();

  if (isLoading) {
    return (
      <div className="mx-auto max-w-[1500px] px-4 py-5 sm:px-6 lg:px-8">
        <Panel>
          <PanelBody>
            <p className="text-sm text-gray-600 dark:text-gray-300">Loading source tables from Redshift...</p>
          </PanelBody>
        </Panel>
      </div>
    );
  }

  if (isError || !workbook) {
    return (
      <div className="mx-auto max-w-[1500px] px-4 py-5 sm:px-6 lg:px-8">
        <Panel>
          <PanelHeader>
            <PanelTitle>Redshift Source Tables Not Connected</PanelTitle>
          </PanelHeader>
          <PanelBody className="space-y-3">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Judgmental Accruals source data must come from Redshift. Runtime mock source tables are disabled.
            </p>
            {workbookError instanceof Error && (
              <p className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:bg-rose-900/20 dark:text-rose-300">
                {workbookError.message}
              </p>
            )}
          </PanelBody>
        </Panel>
      </div>
    );
  }

  const legalTable = workbook.tables.Legal_Contingency_Log;
  const unusualTable = workbook.tables.Unusual_Items_Log;

  return (
    <div className="mx-auto max-w-[1500px] px-4 py-5 sm:px-6 lg:px-8">
      <div className="grid gap-5 lg:grid-cols-[360px_minmax(0,1fr)]">
        <aside className="space-y-5">
          <Panel>
            <PanelHeader>
              <PanelTitle>Workbook Inventory</PanelTitle>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                EC02e_Judgment_Accrual_Log.xlsx
              </p>
            </PanelHeader>
            <PanelBody className="space-y-3">
              {workbook.sourceTabs.map(tab => (
                <div key={tab.name} className="rounded-lg border border-gray-200 p-3 dark:border-dark-border">
                  <div className="flex items-start gap-3">
                    <CircleStackIcon className="mt-0.5 h-5 w-5 text-gray-400 dark:text-gray-500" />
                    <div className="min-w-0">
                      <div className="break-words text-sm font-semibold text-charcoal dark:text-gray-100">{tab.name}</div>
                      <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">{tab.rows} rows x {tab.columns} columns</div>
                    </div>
                  </div>
                  <p className="mt-3 text-sm leading-6 text-gray-600 dark:text-gray-300">{tab.purpose}</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {tab.notes.map(note => <Badge key={note} variant="secondary">{note}</Badge>)}
                  </div>
                </div>
              ))}
            </PanelBody>
          </Panel>
        </aside>

        <div className="space-y-5">
          <Panel>
            <PanelHeader className="flex items-start justify-between gap-4">
              <div>
                <PanelTitle>Legal Contingency Log</PanelTitle>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Rows imported from {workbook.sourceMode}; schema {workbook.redshiftSchema}.
                </p>
              </div>
              <DocumentTextIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" />
            </PanelHeader>
            <PanelBody>
              <TabularDataViewer
                data={legalTable}
                filename="EC02e_Legal_Contingency_Log.csv"
                maxHeight="520px"
              />
            </PanelBody>
          </Panel>

          <Panel>
            <PanelHeader>
              <PanelTitle>Unusual Items Log</PanelTitle>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Controller judgment items requiring HOTL review.</p>
            </PanelHeader>
            <PanelBody>
              <TabularDataViewer
                data={unusualTable}
                filename="EC02e_Unusual_Items_Log.csv"
                maxHeight="420px"
              />
            </PanelBody>
          </Panel>
        </div>
      </div>
    </div>
  );
}
